import { Debugger } from 'springroll';

const BACKGROUND_AUDIO = { id: null, volume: 0.2 };
const SFX_CLICK = 'click';

export default class SoundController {
  constructor({ game }) {
    this.game = game;
    this.sounds = {};
    this.sprites = {};
    this.backgroundMusic = { ...BACKGROUND_AUDIO };
    this._soundVolume = 1;
    this._sfxVolume = 1;
    this._musicVolume = 1;
    this._voVolume = 1;
  }

  startup() {
    this.game.sound.mute = false;
    this.touchLock();
  }

  decode(decodeSounds, callback, callbackContext) {
    this.game.sound.setDecodedCallback(decodeSounds, callback, callbackContext);
  }

  set soundVolume(volume) {
    this.game.sound.volume = volume;
    this._soundVolume = volume;
  }

  set sfxVolume(volume) {
    this._sfxVolume = volume;
    for (const i in this.sounds) {
      if (i !== this.backgroundMusic.id) this.sounds[i].mute = this._sfxVolume === 0;
    }
  }

  set musicVolume(volume) {
    this._musicVolume = volume;
    this._musicVolume === 1 ? this.playBackgroundMusic() : this.pauseBackgroundMusic();
  }

  set voVolume(volume) {
    this._voVolume = volume;
  }

  get allowSFX() {
    return this._sfxVolume === 1;
  }

  get allowMusic() {
    return this._musicVolume === 1;
  }

  get allowVo() {
    return this._voVolume === 1;
  }

  /*
   * Touchlock and AudioContext handling
   */
  touchLock() {
    if (this.game.sound.usingWebAudio && this.game.sound.context.state === 'suspended') {
      Debugger.log('log', 'AudioContext is suspended');
      this.game.sound.setTouchLock(); // doesn't seem to do anything
      this.isTouchLocked = true;
    }
  }

  checkAudioContext() {
    if (this.isTouchLocked && this.game.sound.usingWebAudio) {
      Debugger.log('log', 'AudioContext attempting resume');
      this.game.sound.context.resume().then(() => {
        Debugger.log('log', 'AudioContext resumed successfully');
        this.isTouchLocked = this.game.sound.context.state === 'suspended';
      });
    }
  }

  /**
   * End Touchlock and AudioContext handling
   */

  /*
  =====================================================================
    ADD / REMOVE SOUNDS
  =====================================================================
  */

  addSound(id, assetKey) {
    this.sounds[id] = this.game.make.audio(assetKey);
    this.sounds[id].stop();
  }

  removeSound(id, assetKey) {
    if (this.sounds[id]) {
      this.sounds[id].destroy();
      this.game.cache.removeSound(assetKey);
    }
  }

  /*
  =====================================================================
    ADD / REMOVE AUDIOSPRITES
  =====================================================================
  */

  addAudioSprite({ id }) {
    this.sprites[id] = this.game.add.audioSprite(id);
    // this.sprites[id].allowMultiple = true;
  }

  removeAudioSprite({ id }) {
    if (this.sprites[id]) {
      if (this.sprites[id].isPlaying) {
        this.sprites[id].stop();
      }

      Object.keys(this.sprites[id].sounds).forEach((key) => {
        if (this.sprites[id].sounds[key].isPlaying) {
          this.sprites[id].sounds[key].stop();
        }
        this.sprites[id].sounds[key].destroy();
        this.game.cache.removeSound(key);
        delete this.sprites[id].sounds[key];
      });

      this.game.cache.removeJSON(id);
      this.game.cache.removeSound(id);

      delete this.sprites[id];
    }
  }

  getSfx(id, spriteID) {
    if (this.checkSfxExists(id, spriteID)) {
      return this.sprites[spriteID].sounds[id];
    }
    return null;
  }

  checkSfxExists(id, spriteID) {
    return this.sprites[spriteID] && this.sprites[spriteID].sounds[id];
  }

  sfxExists(id, spriteID) {
    if (!this.checkSfxExists(id, spriteID)) {
      Debugger.log(
        'warn',
        'Cant find ' +
          id +
          ' in audiosprite ' +
          spriteID +
          ', has it been added? (config.json and assetpack)'
      );
      return false;
    }
    return true;
  }

  playSFX(id, opts = {}) {
    opts.volume = (opts.volume || 1) * this._sfxVolume;
    return this.playAudioSprite(id, opts);
  }

  playVO(id, opts = {}) {
    opts.volume = (opts.volume || 1) * this._voVolume;
    return this.playAudioSprite(id, opts);
  }

  playAudioSprite(id, opts = {}) {
    // prettier-ignore
    const { volume = 1, loop = false, ignoreIfPlaying = false, delay = null, onComplete = null, onCompleteScope = null, spriteID = 'generic_audiosprite' } = opts;
    const exists = this.sfxExists(id, spriteID);
    if (!exists) {
      return false;
    }
    // IE hack to not play audiosprite if game paused
    if (Phaser.Device.ie === true && this.game.paused) {
      return;
    }

    this.checkAudioContext();

    if (delay) {
      opts.delay = null;
      this.game.time.events.add(delay, this.playAudioSprite, this, id, opts);
      return true;
    }

    const sound = this.sprites[spriteID];
    const soundObj = sound.get(id);
    if (!this.isTouchLocked) {
      if (ignoreIfPlaying && soundObj.isPlaying) {
        return false;
      }
      soundObj.loop = loop;
      soundObj.markers[id].loop = loop; // need to set loop in markers also
      sound.play(id, volume);
      if (onComplete) {
        soundObj.onStop.addOnce(() => {
          onComplete.call(onCompleteScope);
        });
      }
    } else if (onComplete) {
      // Fire the Signal immediately
      const completeSignal = new Phaser.Signal();
      completeSignal.addOnce(() => {
        onComplete.call(onCompleteScope);
      });
      completeSignal.dispatch();
    } else {
      return false;
    }
    return sound;
  }

  stopAudioSprite(id, opts = {}) {
    const { spriteID = '' } = opts;
    if (this.sprites[spriteID]) {
      const snd = this.sprites[spriteID].get(id);
      if (snd && snd.isPlaying) {
        this.sprites[spriteID].stop(id);
      }
    } else if (this.sounds[id]) {
      if (this.sounds[id].isPlaying) {
        this.sounds[id].stop();
      }
    }
  }

  stopAllSFX() {
    for (const i in this.sprites) {
      if (this.sprites.hasOwnProperty(i)) {
        for (const id in this.sprites[i].sounds) {
          if (this.sprites[i].sounds.hasOwnProperty(id)) {
            if (this.sprites[i].sounds[id].isPlaying) {
              if (id !== SFX_CLICK) {
                this.sprites[i].sounds[id].stop();
              }
            }
          }
        }
      }
    }
  }

  /*
  =====================================================================
    BACKGROUND MUSIC
  =====================================================================
  */
  clearBackgroundMusic() {
    this.backgroundMusic = { ...BACKGROUND_AUDIO };
  }

  setBackgroundMusic(id) {
    if (this.sounds[id]) {
      this.backgroundMusic.id = id;
    } else {
      Debugger.log('warn', `Background music ${id} not found.`);
    }
  }

  stopBackgroundMusic() {
    const { id } = this.backgroundMusic;
    this.stopSound(id);
  }

  playBackgroundMusic() {
    const { id, volume } = this.backgroundMusic;
    const snd = this.sounds[id];
    if (snd) {
      if (this.allowMusic) {
        if (!snd.paused && !snd.isPlaying) {
          snd.play('', 0, volume, true);
          // snd.fadeTo(500, volume);
        } else if (snd.paused) {
          snd.volume = 0;
          snd.resume();
          snd.fadeTo(500, volume);
        }
      }
    }
    if (this.game.paused) {
      this.pauseBackgroundMusic();
    }
  }

  pauseBackgroundMusic() {
    const { id } = this.backgroundMusic;
    const snd = this.sounds[id];
    if (snd) {
      snd.pause();
    }
  }

  stopSound(id) {
    if (this.sounds[id]) {
      this.sounds[id].pausedPosition = 0;
      if (this.sounds[id].isPlaying) {
        this.sounds[id].stop();
      }
    }
  }

  setVolume(id, volume) {
    if (this.backgroundMusic.id === id) {
      this.backgroundMusic.volume = volume;
      this.sounds[id].fadeTo(1000, volume);
    } else {
      this.sounds[id].volume = volume;
    }
  }
}
